import React, { useEffect, useState } from 'react';
import {
  Container, Card, CardBody, CardHeader, Table, Spinner, Button, Progress, Badge, Input,
  Collapse,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import { getMyProjects, getProjectDetails, deleteProject, updateProject, getTeamLeads, updateSubTaskVisibility, updateTaskVisibility } from '../../Api/ApiUrlConst';
import { Avatar, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface TeamLead {
  userID: string;
  name: string;
  department: string;
}

interface SubTask {
  id: number;
  description?: string;
  title?: string;
  dueDate: string;
  status: string;
  priority: string;
  workHours: number;
  percentCompleted: number;
  completionDate: string | null;
  remarks: string;
  assignedToName?: string;
  isVisible?: boolean;
}

interface Task {
  id: number;
  description?: string;
  title?: string;
  dueDate: string;
  status: string;
  priority: string;
  percentCompleted: number;
  completionDate: string;
  remarks: string;
  subTasks: SubTask[];
  assignedToName?: string;
  isVisible?: boolean;
}

interface Project {
  id: number;
  projectID: string;
  projectName: string;
  description: string;
  teamLeadIds: string[];
  teamLeadNames: string[];
  createdByName: string;
  departmentName: string;
  startDate: string;
  endDate: string;
  status: string;
  remarks: string;
  tasks?: Task[];

}
interface Department {
  id: string;
  name: string;
}


const statusColorMap: Record<string, string> = {
  'Yet to Start': 'status-yet-to-start',
  YetToStart: 'status-yet-to-start',

  'On Hold': 'status-on-hold',
  OnHold: 'status-on-hold',

  'Waiting for Input': 'status-waiting-input',
  WaitingForInput: 'status-waiting-input',

  'Modelling in Progress': 'status-modelling-progress',
  ModellingInProgress: 'status-modelling-progress',

  'Modelling ToDo': 'status-modelling-Todo',
  ModellingTodo: 'status-modelling-Todo',

  'Modelling Completed': 'status-modelling-completed',
  ModellingCompleted: 'status-modelling-completed',

  'Drafting in Progress': 'status-drafting-progress',
  DraftingInProgress: 'status-drafting-progress',

  'Drafting ToDo': 'status-drafting-Todo',
  DraftingTodo: 'status-drafting-Todo',

  'Drafting Completed': 'status-drafting-completed',
  DraftingCompleted: 'status-drafting-completed',

  'QC ToDo': 'status-qc-Todo',
  QCTodo: 'status-qc-Todo',

  'QC in Progress': 'status-qc-progress',
  QCInProgress: 'status-qc-progress',

  'QC Completed': 'status-qc-completed',
  QCCompleted: 'status-qc-completed',

  'Client Review in Progress': 'status-client-review-progress',
  ClientReviewInProgress: 'status-client-review-progress',

  'Client Review ToDo': 'status-client-review-Todo',
  ClientReviewTodo: 'status-client-review-Todo',



  'Client Review Completed': 'status-client-review-completed',
  ClientReviewCompleted: 'status-client-review-completed',

  'Revision in Progress': 'status-Revision-progress',
  RevisionInProgress: 'status-Revision-progress',

  'Revision ToDo': 'status-Revision-Todo',
  RevisionTodo: 'status-Revision-Todo',

  'Revision Completed': 'status-Revision-completed',
  RevisionCompleted: 'status-Revision-completed',


  'Final Correction in Progress': 'status-final-correction-progress',
  FinalCorrectionInProgress: 'status-final-correction-progress',

  'Final Correction ToDo': 'status-final-correction-Todo',
  FinalCorrectionTodo: 'status-final-correction-Todo',

  'Final Correction Completed': 'status-final-correction-completed',
  FinalCorrectionCompleted: 'status-final-correction-completed',

  'Not In Scope': 'status-Not-In-Scope',
  NotInScope: 'status-Not-In-Scope',

  Released: 'status-released',
  'Completed & Closed': 'status-completed',
  Completed: 'status-completed',

  Cancelled: 'status-cancelled',
  Issues: 'status-issues',

 CompletedAndReleased: "status-completedandreleased",


  NotApplicable1: "status-Drafting-Released",
};

const formatStatusLabel = (status: string) => {
  const overrides: Record<string, string> = {
    Completed: "Completed",
    YetToStart: "Yet to Start",
    ModellingInProgress: "In Progress",
      NotApplicable:"Not In Scope"

  };

  if (overrides[status]) {
    return overrides[status];
  }

  // generic formatter for the rest
  return status
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/And/g, "&")
    .trim();
};

const priorityColorMap: Record<string, string> = {
  Low: 'priority-low',
  Medium: 'priority-medium',
  High: 'priority-high',
  Critical: 'priority-critical',
};

// --- COMPONENT ---
const PMProjectListComponents: React.FC<{ refreshTrigger: boolean }> = ({ refreshTrigger }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  // 1. New state for the search query
  const [searchQuery, setSearchQuery] = useState('');
  // 2. New state for the filtered projects that will be displayed
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState<string[]>([]);
  const [loadingTasks, setLoadingTasks] = useState<string[]>([]);
  const [expandedTasks, setExpandedTasks] = useState<Record<number, boolean>>({});
  const [allTeamLeads, setAllTeamLeads] = useState<TeamLead[]>([]);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingProjectData, setEditingProjectData] = useState<Partial<Project> | null>(null);
  const [teamLeadModalOpen, setTeamLeadModalOpen] = useState(false);
  const [tempSelectedTeamLeadIds, setTempSelectedTeamLeadIds] = useState<string[]>([]);


  const [isSaving, setIsSaving] = useState(false);

  //Model 
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState<string>('');

  const toggleRemarkModal = (remark?: string) => {
    if (remark) setSelectedRemark(remark);
    setRemarkModalOpen(!remarkModalOpen);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [projectsRes, teamLeadsRes] = await Promise.all([getMyProjects(), getTeamLeads()]);
        const projectData = projectsRes?.data;
        const teamLeadsData = teamLeadsRes?.data;
        setProjects(Array.isArray(projectData) ? projectData : projectData?.$values || []);
        setAllTeamLeads(Array.isArray(teamLeadsData) ? teamLeadsData : teamLeadsData?.$values || []);
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshTrigger]);

  // 3. New useEffect to handle filtering logic
  useEffect(() => {
    const lowercasedQuery = searchQuery.toLowerCase().trim();
    if (!lowercasedQuery) {
      setFilteredProjects(projects); // If search is empty, show all projects
      return;
    }

    const filtered = projects.filter(p => {
      // Check against all relevant fields
      const matchesId = p.projectID.toLowerCase().includes(lowercasedQuery);
      const matchesName = p.projectName.toLowerCase().includes(lowercasedQuery);
      const matchesDept = (p.departmentName || '').toLowerCase().includes(lowercasedQuery);
      const matchesCreator = (p.createdByName || '').toLowerCase().includes(lowercasedQuery);
      const matchesRemarks = (p.remarks || '').toLowerCase().includes(lowercasedQuery);
      const matchesTeamLeads = p.teamLeadNames.some(name => name.toLowerCase().includes(lowercasedQuery));
      return matchesId || matchesName || matchesDept || matchesCreator || matchesRemarks || matchesTeamLeads;
    });

    setFilteredProjects(filtered);
  }, [searchQuery, projects]); // Re-run filter when search query or projects list changes


  // ... (All other handler functions like handleEditClick, handleSaveClick, etc., remain unchanged)
  const handleEditClick = (project: Project) => {
    setEditingProjectId(project.projectID);

    const formattedProject = {
      ...project,
      startDate: new Date(project.startDate).toISOString().split('T')[0],
      endDate: new Date(project.endDate).toISOString().split('T')[0],
      departmentName: project.departmentName || "",
    };

    setEditingProjectData(formattedProject);
    setTempSelectedTeamLeadIds(project.teamLeadIds || []); // ✅ important
  };


  const handleCancelClick = () => {
    setEditingProjectId(null);
    setEditingProjectData(null);
  };

  const handleSaveClick = async () => {
    if (!editingProjectData || !editingProjectId) return;
    if (!editingProjectData.projectName || !editingProjectData.projectID || !editingProjectData.teamLeadIds?.length) {
      return alert("Project ID, Name, and at least one Team Lead are required.");
    }
    if (new Date(editingProjectData.endDate!) <= new Date(editingProjectData.startDate!)) {
      return alert("End Date must be after Start Date.");
    }

    setIsSaving(true);
    try {
      const updateDto = {
        ProjectID: editingProjectData.projectID!,
        ProjectName: editingProjectData.projectName!,
        Description: editingProjectData.description || '',
        TeamLeadIds: editingProjectData.teamLeadIds!,
        StartDate: new Date(editingProjectData.startDate!).toISOString(),
        EndDate: new Date(editingProjectData.endDate!).toISOString(),
        Status: editingProjectData.status!,
        Remarks: editingProjectData.remarks || '',
        DepartmentName: editingProjectData.departmentName || "", // ✅ include in request
      };

      const response = await updateProject(editingProjectId, updateDto);
      let updatedProjectFromServer = response.data.project;

      // ✅ ensure department persists even if backend ignores it
      updatedProjectFromServer = {
        ...updatedProjectFromServer,
        departmentName: editingProjectData.departmentName || updatedProjectFromServer.departmentName || "",
      };

      // ✅ fix missing teamLeadNames after save
      const newTeamLeadNames = updatedProjectFromServer.teamLeadIds?.map(
        (id: string) => allTeamLeads.find(lead => lead.userID === id)?.name || 'Unknown'
      ) || [];
      updatedProjectFromServer.teamLeadNames = newTeamLeadNames;

      setProjects(prev =>
        prev.map(p => p.projectID === editingProjectId ? updatedProjectFromServer : p)
      );

      handleCancelClick();
    } catch (err: any) {
      alert('Failed to save project: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSaving(false);
    }
  };


  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditingProjectData(prev => ({
      ...(prev ?? {}),
      [name]: value,
    }));
  };

  const handleTeamLeadChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
    setEditingProjectData(prev => prev ? { ...prev, teamLeadIds: selectedIds } : null);
  };

  const handleDeleteProject = async (projectID: string, projectName: string) => {
    if (window.confirm(`Are you sure you want to delete "${projectName}"? This cannot be undone.`)) {
      try {
        await deleteProject(projectID);
        setProjects(prev => prev.filter(p => p.projectID !== projectID));
      } catch (err: any) {
        alert('Failed to delete project: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const toggleProjectExpand = async (projectID: string) => {
    if (expandedProjects.includes(projectID)) {
      return setExpandedProjects(prev => prev.filter(p => p !== projectID));
    }
    const project = projects.find(p => p.projectID === projectID);
    if (project?.tasks) {
      return setExpandedProjects(prev => [...prev, projectID]);
    }
    try {
      setLoadingTasks(prev => [...prev, projectID]);
      const res = await getProjectDetails(projectID);
      const tasksArray = Array.isArray(res.data.tasks) ? res.data.tasks : res.data.tasks?.$values || [];
      const normalizedTasks = tasksArray.map((task: any) => ({ ...task, subTasks: Array.isArray(task.subTasks) ? task.subTasks : task.subTasks?.$values || [] }));
      setProjects(prev => prev.map(p => (p.projectID === projectID ? { ...p, tasks: normalizedTasks } : p)));
      setExpandedProjects(prev => [...prev, projectID]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTasks(prev => prev.filter(p => p !== projectID));
    }
  };

  const formatDate = (date?: string | null) => {
    if (!date) return "—";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };


  const toggleTaskVisibility = async (projectID: string, taskId: number, current: boolean) => {
    await updateTaskVisibility(taskId, !current);

    // update local state (NO refetch needed)
    setProjects(prev =>
      prev.map(p =>
        p.projectID !== projectID
          ? p
          : {
            ...p,
            tasks: p.tasks?.map(t =>
              t.id === taskId ? { ...t, isVisible: !current } : t
            ),
          }
      )
    );
  };

  const toggleSubTaskVisibility = async (
    projectID: string,
    taskId: number,
    subTaskId: number,
    current: boolean
  ) => {
    await updateSubTaskVisibility(subTaskId, !current);

    setProjects(prev =>
      prev.map(p =>
        p.projectID !== projectID
          ? p
          : {
            ...p,
            tasks: p.tasks?.map(t =>
              t.id !== taskId
                ? t
                : {
                  ...t,
                  subTasks: t.subTasks.map(st =>
                    st.id === subTaskId ? { ...st, isVisible: !current } : st
                  ),
                }
            ),
          }
      )
    );
  };



  const generateInitials = (name: string) =>
    name?.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase() || '';

  return (
    <Container fluid className="all-projects">
      <Card className="shadow rounded-3 border-0">
        <CardHeader className="bg-primary text-white text-center fw-bold">📋Projects List</CardHeader>
        <CardBody>
          {/* 4. Add the search input field */}
          <div className="mb-3" style={{ width: '500px' }}>
            <Input
              type="text"
              placeholder="Search by Project ID, Name, Department, Team Lead..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="text-center py-5"><Spinner color="primary" /> <p>Loading...</p></div>
          ) : (
            <Table hover responsive className="text-start align-middle project-table">
              <thead className="table-primary">
                <tr>
                  <th style={{ width: '6%' }}></th>
                  <th style={{ width: '10%' }}>Project ID</th>
                  <th style={{ width: '23%' }}>Name</th>
                  <th style={{ width: '20%' }}>Team Leads</th>
                  <th style={{ width: '18%' }}>Status</th>
                  <th style={{ width: '15%' }}>Duration</th>
                  <th style={{ width: '10%' }}>Actions</th>
                  <th></th>

                </tr>
              </thead>
              <tbody>
                {/* 5. Map over 'filteredProjects' instead of 'projects' */}
                {filteredProjects.map((project) => {
                  const isEditing = editingProjectId === project.projectID;
                  const isExpanded = expandedProjects.includes(project.projectID);
                  const projectStatusOptions = [
                    { value: "YetToStart", label: "Yet to Start" },
                    { value: "ModellingInProgress", label: "In Progress" },
                    { value: "Cancelled", label: "Cancelled" },
                    { value: "OnHold", label: "On Hold" },
                    { value: "Completed", label: "Completed" },
                  ];

                  return (
                    <React.Fragment key={project.projectID}>
                      {isEditing ? (
                        <tr className="table-active" >
                          <td><Spinner size="sm" /></td>
                          <td><Input bsSize="sm" name="projectID" value={editingProjectData?.projectID || ''} onChange={handleInputChange} /></td>
                          <td><Input bsSize="sm" name="projectName" value={editingProjectData?.projectName || ''} onChange={handleInputChange} /></td>


                          <td>
                            <Button
                              size="sm"
                              color="secondary"
                              onClick={() => setTeamLeadModalOpen(true)}
                            >
                              {editingProjectData?.teamLeadIds?.length
                                ? `${editingProjectData.teamLeadIds.length} Selected`
                                : "Select Team Leads"}
                            </Button>
                          </td>

                          <td>
                            <Input type="select" bsSize="sm" name="status" value={editingProjectData?.status} onChange={handleInputChange}>
                              <option value="">Select Status</option>
                              {projectStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Input>
                          </td>
                          <td>
                            <Input type="date" bsSize="sm" name="startDate" value={editingProjectData?.startDate} onChange={handleInputChange} />
                            <Input type="date" bsSize="sm" name="endDate" value={editingProjectData?.endDate} onChange={handleInputChange} className="mt-1" />
                          </td>
                          <td className="text-center">
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "8px", // space between buttons
                              }}
                            >
                              <Button
                                size="sm"
                                color="success"
                                style={{
                                  minWidth: "38px",
                                  transition: "all 0.2s ease-in-out",
                                }}
                                onClick={handleSaveClick}
                                disabled={isSaving}
                                title="Save"
                              >
                                <SaveIcon fontSize="small" />
                              </Button>

                              <Button
                                size="sm"
                                color="secondary"
                                style={{
                                  minWidth: "38px",
                                  transition: "all 0.2s ease-in-out",
                                }}
                                onClick={handleCancelClick}
                                title="Cancel"
                              >
                                <CancelIcon fontSize="small" />
                              </Button>
                            </div>
                          </td>


                          <td></td>
                        </tr>
                      ) : (
                        <tr>
                          {/* ... (rest of the display row JSX is unchanged) ... */}
                          <td><Button size="sm" color="link" onClick={() => toggleProjectExpand(project.projectID)}>{isExpanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</Button></td>
                          <td className="fw-semibold">{project.projectID}</td>
                          <td>{project.projectName}</td>
                          <td>
                            <div className="d-flex align-items-start gap-1">
                              {(project.teamLeadNames || []).map((name, i) => (
                                <Tooltip key={i} title={name} arrow>
                                  <span className="d-flex align-items-center">
                                    <Avatar sx={{ width: 27, height: 27, fontSize: 13, marginRight: 1 }}>
                                      {generateInitials(name)}
                                    </Avatar>
                                  </span>
                                </Tooltip>
                              ))}
                            </div>
                          </td>
                          <td>
                            <Badge className={`${statusColorMap[project.status] || 'status-default'} text-capitalize`} pill>
                              {formatStatusLabel(project.status)}
                            </Badge>
                          </td>
                          <td>{formatDate(project.startDate)} - {formatDate(project.endDate)}</td>


                          <td>
                            <Button size="sm" color="info" className="me-2" onClick={() => handleEditClick(project)} title="Edit Project"><EditIcon fontSize="small" /></Button>
                            <Button size="sm" color="danger" onClick={() => handleDeleteProject(project.projectID, project.projectName)} title="Delete Project"><DeleteIcon fontSize="small" /></Button>
                          </td>
                        </tr>
                      )}
                      {/* Expansion logic also remains unchanged */}
                      {isExpanded && !isEditing && (
                        <tr>
                          <td colSpan={9} className="p-0 border-0">
                            <Collapse isOpen={isExpanded} className="p-3 bg-light">
                              {loadingTasks.includes(project.projectID) ? <div className="text-center p-3"><Spinner size="sm" /> <span>Loading Tasks...</span></div> :
                                (project.tasks && project.tasks.length > 0) ? (
                                  <Table size="sm" className="mb-0 bg-white">
                                    <thead className='table-info'>
                                      <tr className='text-start'>
                                        <th></th> {/* visibility */}
                                        <th></th> {/* expand */}
                                        <th>Task</th>
                                        <th>Assigned To</th>
                                        <th>Status</th>
                                        <th>Priority</th>
                                        <th>Due Date</th>
                                        <th>Completion Date</th>
                                        <th style={{ minWidth: 120 }}>Progress</th>
                                      </tr>
                                    </thead>


                                    <tbody>
                                      {project.tasks.map(task => (
                                        <React.Fragment key={task.id}>
                                          <tr
                                            className='text-start'
                                            style={{
                                              opacity: task.isVisible === false ? 0.4 : 1,
                                              display: task.isVisible === false ? 'none' : undefined // OPTIONAL
                                            }}
                                          >
                                            <td>
                                              <Button
                                                size="sm"
                                                color="link"
                                                onClick={() =>
                                                  toggleTaskVisibility(
                                                    project.projectID,
                                                    task.id,
                                                    task.isVisible ?? true
                                                  )

                                                }
                                              >
                                                {task.isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                              </Button>
                                            </td>

                                            <td><Button size="sm" color="link" onClick={() => setExpandedTasks(p => ({ ...p, [task.id]: !p[task.id] }))}>{expandedTasks[task.id] ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</Button></td>
                                            <td className="text-start">{task.title || task.description}</td>
                                            <td className="text-start">{task.assignedToName || '-'}</td>
                                            <td><Badge className={statusColorMap[task.status] || 'secondary'} pill>{formatStatusLabel(task.status)}</Badge></td>
                                            <td><Badge className={priorityColorMap[task.priority] || 'secondary'} pill>{task.priority}</Badge></td>
                                            <td>{formatDate(task.dueDate)}</td>
                                            <td>{formatDate(task.completionDate)}</td>
                                            <td><Progress value={task.percentCompleted} striped animated color={task.percentCompleted === 100 ? "success" : "info"} style={{ height: '20px' }}>{task.percentCompleted}%</Progress></td>
                                          </tr>
                                          {/* SUB-TASK ROW */}
                                          {expandedTasks[task.id] && (
                                            task.subTasks && task.subTasks.length > 0 ? (
                                              task.subTasks.map(sub => (
                                                <tr key={sub.id} style={{ backgroundColor: '#e9f5ff' }}>
                                                  <td></td> {/* Empty cell for expand button column */}
                                                  <td className="ps-4"><b></b> {sub.title || sub.description}</td>
                                                  <td className="text-start">{sub.assignedToName || '-'}</td>
                                                  <td>
                                                    <Badge className={statusColorMap[sub.status] || 'secondary'} pill>
                                                      {formatStatusLabel(sub.status)}
                                                    </Badge>
                                                  </td>
                                                  <td>
                                                    <Badge className={priorityColorMap[sub.priority] || 'secondary'} pill>
                                                      {sub.priority}
                                                    </Badge>
                                                  </td>
                                                  <td>{formatDate(sub.dueDate)}</td>
                                                  <td>{formatDate(sub.completionDate)}</td>
                                                  <td>
                                                    <Progress
                                                      value={sub.percentCompleted}
                                                      striped
                                                      animated
                                                      color={sub.percentCompleted === 100 ? "success" : "info"}
                                                      style={{ height: '20px' }}
                                                    >
                                                      {sub.percentCompleted}%
                                                    </Progress>
                                                  </td>
                                                </tr>
                                              ))
                                            ) : (
                                              <tr style={{ backgroundColor: '#e9f5ff' }}>
                                                <td></td>
                                                <td colSpan={7} className="text-muted ps-4 fst-italic">
                                                  ↳ No sub-tasks for this task.
                                                </td>
                                              </tr>
                                            )
                                          )}
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                  </Table>
                                ) : <div className="text-muted text-center p-3 fst-italic">No tasks found for this project.</div>
                              }
                            </Collapse>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Display a message if no projects match the search */}
                {filteredProjects.length === 0 && !loading && (
                  <tr>
                    <td colSpan={10} className="text-center text-muted fst-italic py-4">
                      No projects found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={remarkModalOpen} toggle={() => toggleRemarkModal()}>
        <ModalHeader toggle={() => toggleRemarkModal()}>Remarks</ModalHeader>
        <ModalBody style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '1.05rem', lineHeight: '1.6' }}>
          {selectedRemark || 'No remarks available.'}
        </ModalBody>
      </Modal>
      <Modal isOpen={teamLeadModalOpen} toggle={() => setTeamLeadModalOpen(false)}>
        <ModalHeader toggle={() => setTeamLeadModalOpen(false)}>
          Select Team Leads
        </ModalHeader>

        <ModalBody>
          {allTeamLeads.map((lead) => (
            <div key={lead.userID} className="form-check mb-2">
              <Input
                type="checkbox"
                className="form-check-input"
                checked={tempSelectedTeamLeadIds.includes(lead.userID)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setTempSelectedTeamLeadIds(prev => [...prev, lead.userID]);
                  } else {
                    setTempSelectedTeamLeadIds(prev =>
                      prev.filter(id => id !== lead.userID)
                    );
                  }
                }}
              />
              <label className="form-check-label ms-2">
                {lead.name} <small className="text-muted">({lead.department})</small>
              </label>
            </div>
          ))}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                setEditingProjectData(prev => ({
                  ...(prev ?? {}),
                  teamLeadIds: tempSelectedTeamLeadIds,
                }));
                setTeamLeadModalOpen(false);
              }}
            >
              Save
            </Button>

            <Button
              color="secondary"
              size="sm"
              onClick={() => setTeamLeadModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </ModalBody>
      </Modal>

    </Container>
  );
};

export default PMProjectListComponents;
